import { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, Tabs, Tab,
} from '@mui/material';
import { portalApi } from '../../services/portalApiClient';
import { API_ENDPOINTS } from '../../config/api';

interface MedicalRecord {
  id: number; visit_date: string; record_type: string; diagnosis: string;
  follow_up_date: string; pet_name: string; species: string; vet_name: string;
}

interface Vaccination {
  id: number; vaccine_name: string; administered_date: string; next_due_date: string;
  manufacturer: string; batch_number: string; site_of_injection: string;
  pet_name: string; species: string; administered_by_name: string;
}

const dueColor = (dateStr: string): 'error' | 'warning' | 'success' => {
  if (!dateStr) return 'success';
  const days = Math.ceil((new Date(dateStr).getTime() - Date.now()) / 86_400_000);
  if (days <= 0) return 'error';
  if (days <= 30) return 'warning';
  return 'success';
};

const PortalMedicalPage = () => {
  const [tab, setTab] = useState(0);
  const [records, setRecords] = useState<MedicalRecord[]>([]);
  const [vaccinations, setVaccinations] = useState<Vaccination[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([
      portalApi.get<{ data: MedicalRecord[] }>(API_ENDPOINTS.PORTAL.MEDICAL_RECORDS),
      portalApi.get<{ data: Vaccination[] }>(API_ENDPOINTS.PORTAL.VACCINATIONS),
    ]).then(([medRes, vacRes]) => {
      setRecords(Array.isArray(medRes.data) ? medRes.data : []);
      setVaccinations(Array.isArray(vacRes.data) ? vacRes.data : []);
    }).catch(() => setError('Failed to load medical records'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>Medical History</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={`Medical Records (${records.length})`} />
        <Tab label={`Vaccinations (${vaccinations.length})`} />
      </Tabs>

      {tab === 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Pet</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Diagnosis</TableCell>
                <TableCell>Vet</TableCell>
                <TableCell>Follow-up</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map(r => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.visit_date ? new Date(r.visit_date).toLocaleDateString() : '-'}</TableCell>
                  <TableCell><strong>{r.pet_name}</strong><br /><Typography variant="caption" color="textSecondary">{r.species}</Typography></TableCell>
                  <TableCell>{r.record_type || '-'}</TableCell>
                  <TableCell>{r.diagnosis || '-'}</TableCell>
                  <TableCell>{r.vet_name || '-'}</TableCell>
                  <TableCell>{r.follow_up_date ? new Date(r.follow_up_date).toLocaleDateString() : '-'}</TableCell>
                </TableRow>
              ))}
              {records.length === 0 && (
                <TableRow><TableCell colSpan={6} align="center">No medical records found</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tab === 1 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Pet</TableCell>
                <TableCell>Vaccine</TableCell>
                <TableCell>Date Given</TableCell>
                <TableCell>Next Due</TableCell>
                <TableCell>Administered By</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vaccinations.map(v => (
                <TableRow key={v.id} hover>
                  <TableCell><strong>{v.pet_name}</strong><br /><Typography variant="caption" color="textSecondary">{v.species}</Typography></TableCell>
                  <TableCell><strong>{v.vaccine_name}</strong>{v.manufacturer ? <><br /><Typography variant="caption">{v.manufacturer}</Typography></> : null}</TableCell>
                  <TableCell>{v.administered_date ? new Date(v.administered_date).toLocaleDateString() : '-'}</TableCell>
                  <TableCell>
                    {v.next_due_date
                      ? <Chip label={new Date(v.next_due_date).toLocaleDateString()} size="small" color={dueColor(v.next_due_date)} />
                      : '-'}
                  </TableCell>
                  <TableCell>{v.administered_by_name || '-'}</TableCell>
                </TableRow>
              ))}
              {vaccinations.length === 0 && (
                <TableRow><TableCell colSpan={5} align="center">No vaccination records found</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default PortalMedicalPage;
